import { createActionGroup, emptyProps, props } from '@ngrx/store';

import * as fromGenerated from '../../_generated';
import * as fromShared from '../shared';

export interface SelectDocumentInChatMessagePayload {
  chatMessageId: number;
  documentUuid: string;
}

export const ChatModalActions = createActionGroup({
  source: 'ChatModal',
  events: {
    'Open Delete Chat Modal': emptyProps(),
    'Close Delete Chat Modal': emptyProps(),
    'Open Rename Chat Modal': emptyProps(),
    'Close Rename Chat Modal': emptyProps(),
  },
});

export const ChatActions = createActionGroup({
  source: 'Chat',
  events: {
    'Select Chat': props<{
      id: number;
    }>(),
    'Unselect Chat': emptyProps(),
    'Toggle Loading': props<{
      isLoading: boolean;
    }>(),
    'Select Empty Chat': emptyProps(),
    'Select Empty Chat With Assistant': props<{
      assistantUuid: string;
    }>(),
    'Maximize Navigation': emptyProps(),
    'Minimize Navigation': emptyProps(),
    'Open Assistant Profile': emptyProps(),
    'Close Assistant Profile': emptyProps(),
    'Toggle Assistant Profile': emptyProps(),
    'Select Document': props<{ documentUuid: string }>(),
    'Update Chat Assistant': props<{
      chatId: number;
      assistantUuid: string | null;
    }>(),
    'Set Last Message Animating': props<{
      isAnimating: boolean;
    }>(),
  },
});

export const ChatsActions = createActionGroup({
  source: 'Chats',
  events: {
    'Load Chats': emptyProps(),
    'Load Chats Success': props<{ chats: fromGenerated.ChatView[] }>(),
    'Load Chats Failure': props<{ error: string }>(),
    'Load Chats For Assistant': props<{ assistantUuid: string }>(),
    'Load Chats For Assistant Success': props<{
      chats: fromGenerated.ChatView[];
    }>(),
    'Load Chats For Assistant Failure': props<{ error: string }>(),
    'Delete Chat': props<{
      id: number;
    }>(),
    'Delete Chat Success': props<{ id: number }>(),
    'Delete Chat Failure': props<{ error: string }>(),
    'Rename Chat': props<{
      id: number;
      title: string;
      previousTitle: string;
      requestId: string;
      /**
       * Dispatch-time ISO timestamp; optimistically bumps the chat's
       * `updatedAt` so it reorders immediately, like the server-side bump.
       */
      renamedAt: string;
      source: 'desktop-inline' | 'mobile-modal';
    }>(),
    'Rename Chat Success': props<{
      id: number;
      title: string;
      requestId: string;
      source: 'desktop-inline' | 'mobile-modal';
    }>(),
    'Rename Chat Failure': props<{
      id: number;
      title: string;
      previousTitle: string;
      requestId: string;
      error: string;
      /**
       * True when a newer rename was dispatched for the same chat before this
       * failure landed — the reducer ignores it and no error toast is shown.
       */
      superseded: boolean;
    }>(),
    'Delete Many Chats Success': props<{ ids: number[] }>(),
  },
});

export const ChatWithMessagesActions = createActionGroup({
  source: 'ChatWithMessages',
  events: {
    'Load ChatWithMessages': props<{
      id: number;
      withoutIsLoading?: boolean;
    }>(),
    'Load ChatWithMessages Success': props<{
      chatWithMessages: fromGenerated.ChatWithMessagesView;
    }>(),
    'Load ChatWithMessages Failure': props<{ error: string }>(),
    'Add ChatMessage': props<{
      chatId: number;
      content: string;
      assistant?:
        | Pick<
            fromGenerated.AssistantView,
            'uuid' | 'description' | 'introMessage' | 'locale'
          >
        | undefined;
      documents?: fromGenerated.DocumentView[] | undefined;
    }>(),
    'Add ChatMessage Success': props<{
      chatWithMessages: fromGenerated.ChatWithMessagesView;
    }>(),
    'Add ChatMessage Failure': props<{
      chatId: number;
      error: string;
    }>(),
    'Dismiss Last ChatMessage Error': props<{
      chatId: number;
    }>(),
    'Received ChatWithMessages from Socket Success': props<{
      chatWithMessages: fromGenerated.ChatWithMessagesView;
    }>(),
    'Received ChatWithMessages from Socket Failure': props<{
      chatWithMessages: fromGenerated.ChatWithMessagesView;
      errorCode: string;
    }>(),
    'Select Document in ChatMessage':
      props<SelectDocumentInChatMessagePayload>(),
    'Unselect Document in ChatMessage': emptyProps(),
    'Request Document Citations': props<{
      chatMessageId: number;
      documentUuid: string;
    }>(),
    'Request Document Citations Failure': props<{ error: string }>(),
    'Received Document Citations from Socket Success': props<{
      chatId: number;
      chatMessageId: number;
      documentCitations: fromGenerated.DocumentCitationView[];
    }>(),
    'Set Citations Loading': props<{
      isLoading: boolean;
    }>(),
    'Request Quick Action': props<{
      chatId: number;
      chatMessageId: number;
      quickAction: fromGenerated.RequestQuickActionDto.QuickActionEnum;
      quickActionMessage: string;
    }>(),
    'Request Quick Action Success': props<{
      chatId: number;
      quickAction: fromGenerated.RequestQuickActionDto.QuickActionEnum;
    }>(),
    'Request Quick Action Failure': props<{ error: string }>(),
    'Add Documents': props<{
      transactionId: string;
      chatId: number;
      assistantUuid: string | null;
      files: fromShared.FileUploadStateFile[];
      documentRole?: fromGenerated.ChatDocumentToCreateDto.RoleEnum;
    }>(),
    'Add Documents Success': props<{
      chatId: number;
      documents: fromGenerated.DocumentView[];
    }>(),
    'Add Documents Failure': props<{
      chatId: number;
      documentUuids: string[];
      error: string;
    }>(),
    'Remove Document': props<{
      chatId: number;
      documentUuid: string;
    }>(),
    'Remove Documents': props<{
      chatId: number;
    }>(),
    'Received Document Status Updated From Socket Success': props<{
      chatUuid: string;
      documentId: string;
      documentStatus: fromGenerated.DocumentView['status'];
    }>(),
  },
});

export const ChatsSocketActions = createActionGroup({
  source: 'ChatsSocket',
  events: {
    Reconnect: emptyProps(),
  },
});
